import React, { useRef, useState } from 'react'
import { Grid, Modal, Button, Form, Spinner, Input } from '@enterprise-ui/canvas-ui-react'
import { FormikValues } from 'formik'
import { useAuth } from '@praxis/component-auth'
import {
  CaseResponse,
  GET_REOPENCODES,
  GetReopenCodes,
  ReopenCodes,
} from './Queries'
import { CaseStatus, CaseType } from '../Services/ConstantData'
import { activityData } from '../Services/saveActivity'
import { useGlobalForm } from '../Context/GlobalFormStateContext'
import { SET_FIELD } from './Constants'
import { useQuery } from '@apollo/client'
import { uniqBy } from 'lodash'
import { Autocomplete } from '@enterprise-ui/canvas-ui-react-autocomplete'
import AttachmentDetails from './AttachmentDetails'
import { useProfile } from '../../Layout/UserProfile/UserProfileProvider'
import useAttachment from '../Hooks/useAttachment'
import { AttachmentProvider } from '../Context/AttachmentContext'

interface MainProp {
  headingText: String
  formik: FormikValues
  reopenDetails: CaseResponse | undefined
  saveStatus: any
}

export interface FormFieldType {
  id: number | string
  value: string
  label: string
}

/** Inner component that lives inside its own AttachmentProvider so the
 *  modal gets isolated attachment state. It exposes the upload function
 *  to the parent ReopenModal via a ref.
 */
interface UploadedFile {
  name: string
  documentType: string
  documentNumber: string
}

const ReopenAttachmentSection: React.FC<{
  isVendor: boolean | undefined
  reasonCode: any
  caseId: string | number | undefined
  vendorNumber: number | undefined
  uploadRef: React.MutableRefObject<(() => Promise<boolean>) | null>
  onExpandToggle?: (expanded: boolean) => void
  onAttachmentStateChange?: (hasFiles: boolean, typeNotSet: boolean) => void
}> = ({ isVendor, reasonCode, caseId, vendorNumber, uploadRef, onExpandToggle, onAttachmentStateChange }) => {
  const { uploadAttachment, attachmentDetails } = useAttachment()
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  // Notify parent about attachment state changes
  React.useEffect(() => {
    if (onAttachmentStateChange) {
      const hasFiles = attachmentDetails.attachments?.length > 0
      const typeNotSet = attachmentDetails.attachmentTypeNotSet
      onAttachmentStateChange(hasFiles, typeNotSet)
    }
  }, [attachmentDetails.attachments, attachmentDetails.attachmentTypeNotSet])

  // Keep the ref in sync so the parent can trigger the upload
  uploadRef.current = async (): Promise<boolean> => {
    if (attachmentDetails.attachments?.length > 0) {
      // Capture file info before upload
      const filesBeforeUpload: UploadedFile[] = attachmentDetails.attachments.map(
        (att: any) => ({
          name: att.file?.name || 'Unknown',
          documentType: att.documentType || '',
          documentNumber: att.documentNumber || '',
        })
      )
      const failedFileNames: string[] = await uploadAttachment(String(caseId), vendorNumber)
      const failedSet = new Set(failedFileNames)
      const successfulFiles = filesBeforeUpload.filter(
        (f) => !failedSet.has(f.name)
      )
      if (successfulFiles.length > 0) {
        setUploadedFiles((prev) => [...prev, ...successfulFiles])
      }
      return failedFileNames.length === 0
    }
    return true // no attachments to upload = success
  }

  const handleUploadClick = async () => {
    if (uploadRef.current) {
      await uploadRef.current()
    }
  }

  return (
    <>
      {uploadedFiles.length > 0 && (
        <div className="hc-pa-normal hc-pb-none">
          <Input.Label className="hc-fs-xs" style={{ fontWeight: 'bold' }}>
            Successfully Uploaded Files
          </Input.Label>
          <div
            style={{
              border: '1px solid #4caf50',
              borderRadius: '4px',
              backgroundColor: '#f1f8e9',
              padding: '8px 12px',
              marginTop: '4px',
            }}
          >
            {uploadedFiles.map((file, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '4px 0',
                  borderBottom:
                    idx < uploadedFiles.length - 1
                      ? '1px solid #c8e6c9'
                      : 'none',
                }}
              >
                <span style={{ color: '#2e7d32', fontSize: '13px' }}>
                  ✓ {file.name}
                </span>
                <span style={{ color: '#666', fontSize: '12px' }}>
                  {file.documentType}
                  {file.documentNumber ? ` | ${file.documentNumber}` : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      <AttachmentDetails
        isVendor={isVendor}
        reasonCode={reasonCode}
        onExpandToggle={onExpandToggle}
      />
      <div className="hc-pa-normal hc-pb-dense hc-pt-none">
        <Grid.Container direction="row-reverse">
          <Grid.Item className="hc-pt-none">
            <Button
              size="dense"
              type="primary"
              onClick={handleUploadClick}
              disabled={
                attachmentDetails.uploadingAttachments ||
                attachmentDetails.attachments?.length === 0 ||
                attachmentDetails.attachmentTypeNotSet
              }
            >
              {attachmentDetails.uploadingAttachments && (
                <Spinner size="dense" className="hc-mt-xd" />
              )}
              Upload New Attachments
            </Button>
            {attachmentDetails.uploadingAttachments && (
              <Input.Label className="hc-fs-overline hc-mt-xd">
                Uploading Files ...
              </Input.Label>
            )}
          </Grid.Item>
        </Grid.Container>
      </div>
    </>
  )
}

const ReopenModal: React.FC<MainProp> = ({
  headingText,
  formik,
  reopenDetails,
  saveStatus,
}) => {
  const { session } = useAuth()
  const { userProfile } = useProfile()
  const uploadRef = useRef<(() => Promise<boolean>) | null>(null)
  const userName = session?.userInfo?.fullName ?? ''
  const activityDescription = 'Case was reopened'
  const [state, dispatch] = useGlobalForm()
  const { existingCaseData } = state
  const [reopenReason, setReopenReason] = useState<FormFieldType>({
    id: '',
    value: '',
    label: '',
  })
  const [isAttachmentExpanded, setIsAttachmentExpanded] = useState(true)
  const [hasUnuploadedFiles, setHasUnuploadedFiles] = useState(false)
  const [attachmentTypeNotSet, setAttachmentTypeNotSet] = useState(false)

  const { data: reopenCodesData } = useQuery<GetReopenCodes>(
    GET_REOPENCODES,
    {},
  )

  // Debug logging
  React.useEffect(() => {
    console.log('ReopenModal - existingCaseData:', existingCaseData)
  }, [existingCaseData])

  const justification = formik.values.reopenDetails?.justificationForReopen || ''
  const isFormValid = reopenReason.value && 
                      justification.trim().length >= 30 &&
                      !(hasUnuploadedFiles && attachmentTypeNotSet)

  function setReopenValues() {
    if (
      reopenDetails?.resolutionAmount ||
      reopenDetails?.resolutionText ||
      reopenDetails?.resolutionCode ||
      reopenDetails?.resolutionDate ||
      reopenDetails?.claimDetails?.referenceDocumentNumber ||
      reopenDetails?.disputeDetails?.newDocumentNumber
    ) {
      formik.setFieldValue('reopenDetails.reopenDate', new Date().toISOString())
      formik.setFieldValue('reopenDetails.reopenUser', session?.userInfo?.lanId)
      formik.setFieldValue(
        'reopenDetails.previousResolutionDate',
        reopenDetails?.resolutionDate,
      )
      formik.setFieldValue(
        'reopenDetails.previousResolutionUser',
        reopenDetails?.userProfile?.userId,
      )
      formik.setFieldValue(
        'reopenDetails.previousResolutionCode',
        reopenDetails?.resolutionCode,
      )
      formik.setFieldValue(
        'reopenDetails.previousResolutionCodeText',
        reopenDetails?.resolutionCodeText,
      )
      formik.setFieldValue(
        'reopenDetails.previousResolutionText',
        reopenDetails?.resolutionText === null
          ? undefined
          : reopenDetails.resolutionText,
      )
      formik.setFieldValue(
        'reopenDetails.previousResolutionAmount',
        reopenDetails?.resolutionAmount,
      )
      if (reopenDetails?.caseType === CaseType.INTERNALPROCESSING) {
        formik.setFieldValue(
          'reopenDetails.previousResolutionDocumentNumber',
          reopenDetails?.claimDetails?.referenceDocumentNumber,
        )
      } else {
        formik.setFieldValue(
          'reopenDetails.previousResolutionDocumentNumber',
          reopenDetails?.disputeDetails?.newDocumentNumber,
        )
      }
      formik.setFieldValue('resolutionDate', null)
      formik.setFieldValue('resolutionCode', undefined)
      formik.setFieldValue('resolutionCodeText', null)
      formik.setFieldValue('resolutionText', undefined)
      formik.setFieldValue('resolutionAmount', null)
      formik.setFieldValue('claimDetails.referenceDocumentNumber', null)
      formik.setFieldValue('disputeDetails.newDocumentNumber', null)
      formik.setFieldValue('claimDetails.referenceDocumentCreateDate', null)
      formik.setFieldValue('lanId', null)
      formik.setFieldValue('status', CaseStatus.REOPEN)
    } else {
      formik.setFieldValue('status', CaseStatus.REOPEN)
      formik.setFieldValue('reopenDetails.reopenDate', new Date().toISOString())
      formik.setFieldValue('reopenDetails.reopenUser', session?.userInfo?.lanId)
    }
    saveActivty()
  }

  async function saveActivty() {
    formik.setFieldValue('isReopen', true)
    if (reopenReason && reopenReason.value) {
      formik.setFieldValue('reopenDetails.reopenReason', reopenReason.value)
      formik.setFieldValue('reopenDetails.reopenReasonId', reopenReason.id)
    }
    // lastResolutionComment and justificationForReopen are already set via onChange handlers
    const adAuditEntry = activityData(
      userName,
      activityDescription,
      formik.values,
    )
    formik.setFieldValue('auditEntries', adAuditEntry)

    // Upload attachments if any were added
    if (uploadRef.current) {
      const uploadSuccess = await uploadRef.current()
      if (!uploadSuccess) {
        // Upload failed – don't proceed with the reopen submit
        return
      }
    }

    clearValues()
    saveStatus()
  }

  function getReopenCodeList(
    reopenCodesList: ReopenCodes[] | undefined,
  ): FormFieldType[] {
    if (!reopenCodesList) return []
    return uniqBy(reopenCodesList, 'reopenCodeText')
      .filter((value) => value.active !== false)
      .map(({ reopenCodeId, reopenCodeText, reopenCodeDescription }) => ({
        id: reopenCodeId ?? '',
        value: reopenCodeText ?? '',
        label: reopenCodeDescription ?? reopenCodeText ?? '',
      }))
  }

  const clearValues = () => {
    dispatch({
      type: SET_FIELD,
      payload: [{ id: 'reOpenModalFlag', value: false }],
    })
    setReopenReason({ id: '', value: '', label: '' })
  }
  return (
    <div>
      <Modal
        headingText={`Reopen Case - ${reopenDetails?.caseId || ''}`}
        isVisible={state.reOpenModalFlag}
        onRefuse={() => clearValues()}
        className={`reopenModal${isAttachmentExpanded ? ' reopenModal--expanded' : ''}`}
      >
        <div className="hc-pa-normal">
          <Grid.Container className="hc-pa-md">
            <Grid.Item xs={12}>
              <Grid.Container>
                <Grid.Item xs={8}>
                  <Autocomplete
                    id="reopenCodeList"
                    label={'Select the reason for reopening this case?'}
                    onUpdate={(id, value) => {
                      setReopenReason(value || { id: '', value: '', label: '' })
                    }}
                    options={
                      getReopenCodeList(reopenCodesData?.getReopenCodes) ?? []
                    }
                  />
                </Grid.Item>
              </Grid.Container>
            </Grid.Item>
            <Grid.Item xs={12} className="hc-mt-normal">
              {reopenReason.value && (
                <div
                  style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    backgroundColor: '#f9f9f9',
                    padding: '16px',
                    marginTop: '12px',
                  }}
                >
                  <div style={{ marginBottom: '16px' }}>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: 'bold',
                        fontSize: '14px',
                      }}
                    >
                      Last Resolution Comment
                    </label>
                    <div
                      style={{
                        padding: '12px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #d0d0d0',
                        borderRadius: '4px',
                        minHeight: '80px',
                        wordWrap: 'break-word',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {existingCaseData?.resolutionText ||
                        reopenDetails?.resolutionText ||
                        'No previous resolution comment'}
                    </div>
                  </div>
                  <div>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: '4px',
                        fontWeight: 'bold',
                        fontSize: '14px',
                      }}
                    >
                      Justification for Reopen
                      <span style={{ color: '#d32f2f' }}>*</span>
                    </label>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#666',
                        marginBottom: '12px',
                        fontStyle: 'italic',
                      }}
                    >
                      Please explain why you believe the resolution is incorrect
                      or provide the reason for reopening this case.
                    </div>
                    <textarea
                      id="justificationForReopen"
                      value={
                        formik.values.reopenDetails?.justificationForReopen ||
                        ''
                      }
                      minLength={30}
                      onChange={(e: any) => {
                        formik.setFieldValue(
                          'reopenDetails.justificationForReopen',
                          e.target.value,
                        )
                      }}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: `1px solid ${(formik.values.reopenDetails?.justificationForReopen || '').trim().length < 30 ? '#d32f2f' : '#d0d0d0'}`,
                        borderRadius: '4px',
                        fontFamily: 'inherit',
                        fontSize: '14px',
                        minHeight: '120px',
                        resize: 'vertical',
                      }}
                      placeholder="Enter your justification"
                    />
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#666',
                        textAlign: 'right',
                        marginTop: '2px',
                      }}
                    >
                      {
                        (
                          formik.values.reopenDetails?.justificationForReopen ||
                          ''
                        ).length
                      }
                      /30
                    </div>
                    {!formik.values.reopenDetails?.justificationForReopen?.trim() ? (
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#d32f2f',
                          marginTop: '4px',
                        }}
                      >
                        This field is required
                      </div>
                    ) : (
                        formik.values.reopenDetails?.justificationForReopen ||
                        ''
                      ).trim().length < 30 ? (
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#d32f2f',
                          marginTop: '4px',
                        }}
                      >
                        Justification must be at least 30 characters
                      </div>
                    ) : null}
                  </div>
                  <div style={{ marginTop: '16px' }}>
                    <AttachmentProvider>
                      <ReopenAttachmentSection
                        isVendor={userProfile?.isVendor}
                        reasonCode={reopenDetails?.reasonCode}
                        caseId={reopenDetails?.caseId}
                        vendorNumber={reopenDetails?.vendorNumber}
                        uploadRef={uploadRef}
                        onExpandToggle={setIsAttachmentExpanded}
                        onAttachmentStateChange={(hasFiles, typeNotSet) => {
                          setHasUnuploadedFiles(hasFiles)
                          setAttachmentTypeNotSet(typeNotSet)
                        }}
                      />
                    </AttachmentProvider>
                  </div>
                </div>
              )}
            </Grid.Item>
            <Grid.Item xs={12}>
              <Grid.Container align="center">
                <Grid.Item xs></Grid.Item>
                <Grid.Item justify="flex-end">
                  <Grid.Container direction="row-reverse" spacing="dense">
                    <Grid.Item>
                      <Button
                        type="primary"
                        disabled={!isFormValid}
                        onClick={() => {
                          setReopenValues()
                        }}
                      >
                        Submit Reopen Request
                      </Button>
                    </Grid.Item>
                    <Grid.Item>
                      <Button
                        onClick={() => {
                          clearValues()
                        }}
                      >
                        Cancel
                      </Button>
                    </Grid.Item>
                  </Grid.Container>
                </Grid.Item>
              </Grid.Container>
            </Grid.Item>
          </Grid.Container>
        </div>
      </Modal>
    </div>
  )
}

export default ReopenModal
